import { Button, TextField } from "../ui";
import { useState } from "react";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import type { TrackMyOrdersFormData } from "../types";

interface Props {
  data: TrackMyOrdersFormData;
  sessionToken: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TrackMyOrdersFormCard({ data, sessionToken }: Props) {
  const headline = useLabel("headline", data.headline || "Track My Order");
  const orderNumberLabel = useLabel("orderNumberLabel", data.orderNumberLabel || "Order Number");
  const emailLabel = useLabel("emailLabel", data.emailLabel || "Email");
  const orderNumberPlaceholder = useLabel(
    "orderNumberPlaceholder",
    data.orderNumberPlaceholder || "Type an order number",
  );
  const emailPlaceholder = useLabel(
    "emailPlaceholder",
    data.emailPlaceholder || "Type an email",
  );
  const submitLabel = useLabel("submitLabel", data.submitLabel || "Track Order");
  const trackingLabel = useLabel("tracking", "Tracking...");
  const orderNumberRequired = useLabel(
    "orderNumberRequired",
    "Order number is required.",
  );
  const emailRequired = useLabel("emailRequired", "Email is required.");
  const emailInvalid = useLabel("emailInvalid", "Please enter a valid email.");

  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ orderNumber?: string; email?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!orderNumber.trim()) next.orderNumber = orderNumberRequired;
    if (!email.trim()) next.email = emailRequired;
    else if (!EMAIL_RE.test(email.trim())) next.email = emailInvalid;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    // 3st 주문정보 §4-3(260612): 말풍선은 고정문구 '주문정보 입력'만 노출.
    // orderNumber/email 은 말풍선이 아닌 toolCall.args 로 전달(tool_directive 가 Planner 우회).
    void ctaTranslate({
      code: "guest_order.enter_order_information",
      sessionToken,
      fallback: "Enter order information",
      toolCall: {
        tool: "commerce_get_delivery_tracking",
        args: { orderNumber: orderNumber.trim(), email: email.trim() },
      },
    });
  };

  return (
    <div className="ac-track-form">
      <h2 className="ac-track-form__title">{headline}</h2>

      <div className="ac-track-form__field">
        <TextField
          label={orderNumberLabel}
          required
          type="text"
          value={orderNumber}
          placeholder={orderNumberPlaceholder}
          status={errors.orderNumber ? "error" : "default"}
          assistiveText={errors.orderNumber}
          onChange={(e) => {
            setOrderNumber(e.target.value);
            if (errors.orderNumber) setErrors((p) => ({ ...p, orderNumber: undefined }));
          }}
        />
      </div>

      <div className="ac-track-form__field">
        <TextField
          label={emailLabel}
          required
          type="email"
          value={email}
          placeholder={emailPlaceholder}
          status={errors.email ? "error" : "default"}
          assistiveText={errors.email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
          }}
        />
      </div>

      <Button
        variant="primary"
        size="sm"
        fullWidth
        disabled={submitting}
        onClick={handleSubmit}
      >
        {submitting ? trackingLabel : submitLabel}
      </Button>
    </div>
  );
}
